import React, { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom';
import { getCompanyDetailsApi, getStatesData, handleKeyDown, updateCompanyApi } from '_services';
import { CityDropdownForTrip, CommonInput, CropModal, CustomSelect } from 'components';
import Tab from 'components/common/Tab'
import ImageCropModal from 'components/ImageModify/ImageCropModal';
import { constant } from 'constants/constant';
import { UserContext } from 'context';
import { companyImageUpload } from 'features';
import { errorSchema, utils } from 'helper';
import useDocumentTitle from 'hooks/useDocumentTitle';
import { Checkbox } from 'pages/User/AddUser';
import { Accordion } from 'react-bootstrap';
import toast from 'react-hot-toast';
import { LuUpload } from "react-icons/lu";
import { useDispatch } from 'react-redux';
import StorageService from 'utils/StorageServices';
import './firm.css'
import { getCityLocationList } from '_services/accountsServices/cityLocationService';
import { createFirm, getFirm, getSingleFirm, updateFirm } from '_services/accountsServices/firmServices';
import { Undo2 } from 'lucide-react';

const useQuery = () => {
    return new URLSearchParams(useLocation().search);
};

const FirmProfileDetails = ({ data }) => {
    const dispatch = useDispatch();
    useDocumentTitle("Company | T360");

    const query = useQuery();
    const navigate = useNavigate();

    // get component name
    const getName = query.get('name');
    const getId = query.get('id');
    const isDetails = true; // getId && (getName === 'details');
    const isEditAction = false; // getId && (getName === 'edit');

    const object = {
        company_id: StorageService.getCompanyId(),
        firm: "",
        entity_type: '',
        contact_no: "",
        alt_contact_no: "",
        email_id: "",
        website: "",
        address_line1: "",
        city: {},
        state: {},
        gst_type: {},
        gst_state: {},
        gst_no: "",
        pan_no: "",
        fssai_licence: "",
        firm_type: []
        // pincode: "",
    }

    const {
        masterData,
        setMasterData,
        cities,
        setCities,
    } = useContext(UserContext);
    const [form, setForm] = useState(object)
    const [formError, setFormError] = useState("");
    const [entityList, setEntityList] = useState([])
    const [isLoading, setIsLoading] = useState(false)
    const companyRef = useRef(null)
    const [isEqal, setIsEqal] = useState(false)
    const [croppedImage, setCroppedImage] = useState(null);
    const [imageLoading, setImageLoading] = useState(false)
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [originalImage, setOriginalImage] = useState(
        null
    );

    const [gstTypeList, setGstTypeList] = useState([]);

    useEffect(() => {
        if (masterData) {
            setEntityList(masterData?.entity_type_list?.map((i) => {
                return {
                    label: i?.name,
                    value: i?.id
                }
            }))
            setGstTypeList(masterData?.gst_type_list?.map((el) => {
                return {
                    label: el?.name,
                    value: el?.id
                }
            }))
        }
    }, [masterData])

    useEffect(() => {
        const isEqual = JSON.stringify(companyRef.current) === JSON.stringify(form);
        setIsEqal(isEqual);
    }, [form]);

    const handleOnChange = async (name, value) => {
        const stateObj = { ...form, [name]: value };
        if (name === 'city') {
            stateObj['state'] = { label: value?.stateName, value: value?.stateName };
        }
        if (name === "gst_no" && value.length === 15) {
            let gstCode = parseInt(value.slice(0, 2), 10);
            stateObj['gst_state'] = utils.codeToStateNameObject[gstCode];
        }
        setForm(stateObj);

        if (!utils.isObjectKeyEmpty(formError)) {
            const validationResult = await utils.checkFormError(stateObj, errorSchema.addFirmSchema);
            if (validationResult === true) {
                setFormError({});
            } else {
                setFormError(validationResult);
            }
        }
    };

    const handleCheckboxChange = (e, item) => {
        const isChecked = e.target.checked;
        if (isChecked) {
            // Add item if checked and not already present
            setForm((prevForm) => ({
                ...prevForm,
                firm_type: [...prevForm.firm_type, item._id,],
            }));
        } else {
            // Remove item if unchecked
            setForm((prevForm) => ({
                ...prevForm,
                firm_type: prevForm.firm_type.filter(
                    (type) => type !== item._id
                ),
            }));
        }
    };

    const handleSubmit = async () => {
        const payload = {
            ...form,
            gst_type: form?.gst_type?.value,
            city: { label: form?.city?.name, value: form?.city?.name }
        }
        const validationResult = await utils.checkFormError(form, errorSchema.addFirmSchema);
        setFormError(validationResult)
        if (validationResult == true) {
            setIsLoading(true)
            try {
                if (isEditAction) {
                    const res = await updateFirm(payload)
                    if (res?.responseCode == 200) {
                        setIsLoading(false)
                        if (res?.responseMessage) {
                            toast.success(res?.responseMessage)
                        }
                        setForm({});
                        navigate('/setting/firm')
                        companyRef.current = { ...form }
                    } else {
                        setIsLoading(false)
                        setCroppedImage(null);
                        if (res?.responseMessage) {
                            toast.error(res?.responseMessage)
                        }
                    }
                } else {
                    const res = await createFirm(payload)
                    if (res?.responseCode == 200) {
                        setIsLoading(false)
                        if (res?.responseMessage) {
                            toast.success(res?.responseMessage)
                        }
                        setForm({});
                        navigate('/setting/firm')
                        companyRef.current = { ...form }
                    } else {
                        setIsLoading(false)
                        setCroppedImage(null);
                        if (res?.responseMessage) {
                            toast.error(res?.responseMessage)
                        }
                    }
                }
            } catch (error) {
                setIsLoading(false)
            }
        }
    }

    async function createFile(path) {
        setImageLoading(true)
        let response = await fetch(path);
        let data = await response.blob()
        let metadata = {
            type: 'image/jpeg'
        };
        let file = new File([data], "test.jpg", metadata);
        const formData = new FormData()
        formData.append("logo", file)
        if (formData) {
            const res = await dispatch(companyImageUpload(formData));
            if (res?.payload?.responseCode === 200) {
                setForm({ ...form, logo: res?.payload?.data?.path })
                setImageLoading(false)
                setIsModalOpen(false);
            }
        }
        setIsModalOpen(false);

    }

    const handleCloseModal = () => {
        setIsModalOpen(false);
    };

    const handleCropComplete = async (croppedImageUrl) => {
        setCroppedImage(croppedImageUrl);
        await createFile(croppedImageUrl)
    };

    const handleFileChange = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = () => {
            if (typeof reader.result === "string") {
                setOriginalImage(reader.result);
                setIsModalOpen(true);
            }
            e.target.value = null;
        };
        reader.readAsDataURL(file);
    };

    const fetchFromCity = useCallback(async (search, page, limit) => {
        try {
            const response = await getCityLocationList({
                countryCode: "IN",
                stateCode: "",
                offset: page,
                limit,
                search,
            });

            if (response?.responseCode === 200) {
                const cities = response.data.all_cities.map(city => ({
                    ...city,
                    _id: city.name,
                    label: city.name, // `${city.name}, ${city.stateName}`
                }));

                const result = {
                    items: cities,
                    total: response.data.total_record,
                };
                return result;
            }
            toast.error(response?.responseMessage);
            return { items: [], total: 0 };
        } catch (error) {
            return { items: [], total: 0 };
        }
    }, []);

    const fetchState = useCallback(async (search, page, limit) => {
        try {
            const response = await getStatesData({
                countryCode: "IN",
                stateCode: "",
                offset: page,
                limit,
                search,
            });

            if (response?.responseCode === 200) {
                const cities = response.data.map(city => ({
                    ...city,
                    _id: city._id,
                    label: city.name,
                }));

                const result = {
                    items: cities,
                    total: response.data.total_record,
                };
                return result;
            }
            toast.error(response?.responseMessage);
            return { items: [], total: 0 };
        } catch (error) {
            return { items: [], total: 0 };
        }
    }, []);

    const getSingleItem = async () => {
        // right now i am using list api because i don't have single item get api,
        try {
            let object = {
                company_id: StorageService.getCompanyId(),
                firm_id: getId,
            }
            const res = await getSingleFirm(object);
            if (res?.responseCode == 200) {
                let findObject = res?.data?.firm; // ?.find((el) => el?._id === getId)
                if (findObject?._id) {
                    const details = {
                        firm: findObject?.firm || "",
                        contact_no: findObject?.contact_no || "",
                        alt_contact_no: findObject?.alt_contact_no || "",
                        email_id: findObject?.email_id || "",
                        entity_type: findObject?.entity_type,
                        website: findObject?.website || "",
                        gst_no: findObject?.gst_no || '',
                        gst_type: gstTypeList.find((el) => el?.value === findObject?.gst_type),
                        gst_state: findObject?.gst_state || {},
                        pan_no: findObject?.pan_no || "",
                        fssai_licence: findObject?.fssai_licence || "",
                        address_line1: findObject?.address_line1 || "",
                        address_line2: findObject?.address_line2 || "",
                        state: findObject?.state,
                        city: { ...findObject?.city, name: findObject?.city?.label },
                        pincode: findObject?.pincode || "",
                        firm_type: findObject?.firm_type,
                        logo: findObject?.logo || "",
                        firm_id: findObject?._id,
                    }
                    setForm(details);
                    // setForm((pre) => {
                    //   companyRef.current = { ...pre, ...details }
                    //   return ({ ...pre, ...details })
                    // })
                }
            }
        } catch (error) { }
    }

    useEffect(() => {
        setForm(data);
    }, [data])

    return (
        <div className='__profilCmp_main' >
            <div className='__setsContent_prt'>
                <div className='__stContent'>
                    <div className='__cprPr_otr'>
                        <div className='__cprPr_inr'>
                            <div>
                                <p className='__cpl_txt'>Firm Logo</p>
                                <div className='__cpr_lg_bx' >
                                    <div>
                                        <input
                                            type="file"
                                            id='_cpLogo'
                                            style={{ display: 'none' }}
                                            onChange={handleFileChange}
                                            disabled={isDetails ? true : false}
                                        />
                                        <label htmlFor="_cpLogo" className='_cpLogo_upload'>

                                            {croppedImage || form?.logo ? (
                                                <img className="_cpLogo_upload" src={`${masterData?.s3_url}${form?.logo}` || croppedImage} alt="Cropped Preview" />
                                            ) : (
                                                <LuUpload className="" />
                                            )}
                                        </label>
                                    </div>
                                    <p>Preferred Image Dimensions: 240 x 240 pixels @ 72 DPI Maximum File Size: 1MB</p>
                                </div>
                            </div>

                            <div>
                                <div className='__cprPr_fxOtr'>
                                    <div className='__cprPr_fxInr'>
                                        <div className='__cp_flex_half'>
                                            <CommonInput
                                                type="text"
                                                label="Firm Name"
                                                placeholder="Firm Name"
                                                value={form?.firm ? form?.firm : ""}
                                                name="firm"
                                                maxLength="50"
                                                onInput={utils.maxLengthCheck}
                                                disabled={isDetails ? true : false}
                                                focus={!!formError?.firm}
                                                error={formError.firm}
                                                onChange={(e) => utils.handleNameInputChange(e, handleOnChange)}
                                            // onChange={({ target: { name, value } }) =>
                                            //   handleOnChange(name, value)
                                            // }
                                            />
                                        </div>

                                        <div className='__cp_flex_half'>
                                            <CustomSelect
                                                label="Entity Type"
                                                placeholder="Select Entity"
                                                options={entityList}
                                                disabled={isDetails ? true : false}
                                                onSelect={(data) => handleOnChange("entity_type", data?.value)}
                                                value={entityList?.find((i) => i.value == form?.entity_type)}
                                                error={formError["entity_type"]}
                                            />
                                        </div>

                                        <div className='__cp_flex_half'>
                                            <CommonInput
                                                type="text"
                                                label="Mobile Number"
                                                placeholder="Mobile Number"
                                                value={form?.contact_no ? form?.contact_no : ""}
                                                maxLength={10}
                                                onKeyDown={handleKeyDown}
                                                disabled={isDetails ? true : false}
                                                name="contact_no"
                                                focus={!!formError?.contact_no}
                                                error={formError.contact_no}
                                                onChange={({ target: { name, value } }) => {
                                                    if (/^\d*$/.test(value)) {
                                                        handleOnChange(name, +value);
                                                    }
                                                }}
                                            />
                                        </div>

                                        <div className='__cp_flex_half'>
                                            <CommonInput
                                                type="text"
                                                label="Alternate Number"
                                                placeholder="Alternate Number"
                                                value={form?.alt_contact_no ? form?.alt_contact_no : ""}
                                                maxLength={10}
                                                name="alt_contact_no"
                                                disabled={isDetails ? true : false}
                                                focus={!!formError?.alt_contact_no}
                                                error={formError.alt_contact_no}
                                                onChange={({ target: { name, value } }) => {
                                                    // Remove all non-digit characters (anything that's not 0-9)
                                                    let cleanedValue = value.replace(/\D/g, '');

                                                    // Remove '91' or '+91' from start if present and extra digits exist
                                                    if (cleanedValue.startsWith('91') && cleanedValue.length > 10) {
                                                        cleanedValue = cleanedValue.substring(2);
                                                    }

                                                    // Allow only if the result is 10 digits or fewer
                                                    if (/^\d{0,10}$/.test(cleanedValue)) {
                                                        handleOnChange(name, +cleanedValue);
                                                    }
                                                }}
                                            />
                                        </div>

                                        <div className='__cp_flex_half'>
                                            <CommonInput
                                                type="text"
                                                label="Email ID"
                                                placeholder="Email ID"
                                                value={form?.email_id ? form?.email_id : ""}
                                                name="email_id"
                                                disabled={isDetails ? true : false}
                                                maxLength="50"
                                                onInput={utils.maxLengthCheck}
                                                // onKeyDown={handleKeyDown}
                                                focus={!!formError?.email_id}
                                                error={formError.email_id}
                                                onChange={({ target: { name, value } }) => {
                                                    handleOnChange(name, value);
                                                }}
                                            />
                                        </div>

                                        <div className='__cp_flex_half'>
                                            <CommonInput
                                                type="text"
                                                label="Website"
                                                placeholder="Website"
                                                value={form?.website ? form?.website : ""}
                                                name="website"
                                                disabled={isDetails ? true : false}
                                                maxLength="100"
                                                onInput={utils.maxLengthCheck}
                                                // onKeyDown={handleKeyDown}
                                                focus={!!formError?.website}
                                                error={formError.website}
                                                onChange={({ target: { name, value } }) => {
                                                    handleOnChange(name, value);
                                                }}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div>
                                <p className='__cp_head'>Add Address</p>
                                <div className='__cprPr_fxOtr'>
                                    <div className='__cprPr_fxInr'>
                                        <div className='__cp_flex_half'>
                                            <CommonInput
                                                type="text"
                                                label="Address"
                                                placeholder="Address"
                                                name='address_line1'
                                                disabled={isDetails ? true : false}
                                                maxLength="80"
                                                onInput={utils.maxLengthCheck}
                                                // onKeyDown={handleKeyDown}
                                                value={form?.address_line1 ? form?.address_line1 : ""}
                                                focus={!!formError?.address_line1}
                                                error={formError.address_line1}
                                                onChange={({ target: { name, value } }) => {
                                                    handleOnChange(name, value)
                                                }}
                                            />
                                        </div>

                                        <div className='__cp_flex_half'>
                                            <CommonInput
                                                type="text"
                                                placeholder="PIN Code"
                                                label="PIN Code"
                                                name='pincode'
                                                maxLength={6}
                                                disabled={isDetails ? true : false}
                                                value={form.pincode}
                                                onKeyDown={handleKeyDown}
                                                focus={!!(typeof formError === "object" && formError?.pincode)}
                                                error={!!(typeof formError === "object") ? formError?.pincode : ""}
                                                onChange={({ target: { name, value } }) => {
                                                    if (/^\d*$/.test(value)) {
                                                        handleOnChange(name, value);
                                                    }
                                                }}
                                            />
                                        </div>
                                    </div>

                                    <div className='__cprPr_fxInr'>
                                        <div className='__cp_flex_half'>
                                            <CityDropdownForTrip
                                                label=" City"
                                                placeholder=" City"
                                                fetchData={fetchFromCity}
                                                pageSize={20}
                                                disabled={isDetails ? true : false}
                                                selectedItem={form.city}
                                                onChange={(data) => handleOnChange('city', data)}
                                                error={formError["city.label"]}
                                                errorMessage={formError["city.label"]}
                                            />
                                        </div>

                                        <div className='__cp_flex_half'>
                                            <CustomSelect
                                                options={[]}
                                                label="State"
                                                placeholder="State"
                                                value={form?.state}
                                                disabled={true}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div>
                                <p className='__cp_head'> Add Business Registration Details </p>
                                <div className='__cprPr_fxOtr'>
                                    <div className='__cprPr_fxInr'>
                                        <div className='__cp_flex_half'>
                                            <CustomSelect
                                                label="GST Type"
                                                placeholder="GST Type"
                                                name="gst_type"
                                                options={gstTypeList}
                                                disabled={isDetails ? true : false}
                                                value={form?.gst_type}
                                                onSelect={(data) => handleOnChange('gst_type', data)}
                                                error={formError["gst_type.label"]}
                                            />
                                        </div>

                                        <div className='__cp_flex_half'>
                                            {/* { (form?.gst_type?.value === 2 || form?.gst_type?.value === 3) && ( */}
                                            <CommonInput
                                                type="text"
                                                label="GST Number"
                                                placeholder="GST Number"
                                                value={form?.gst_no ? form?.gst_no : ""}
                                                name="gst_no"
                                                focus={!!formError?.gst_no}
                                                error={formError.gst_no}
                                                disabled={isDetails ? true : false}
                                                maxLength={15}
                                                onChange={({ target: { name, value } }) => {
                                                    const cleanedValue = value.replace(/[^a-zA-Z0-9 ]+/g, "");
                                                    if (typeof handleOnChange === "function") {
                                                        // handleOnChange(name, cleanedValue);
                                                        const upperValue = cleanedValue.toUpperCase();
                                                        handleOnChange(name, upperValue)
                                                    }
                                                }}
                                            />
                                            {/* )} */}
                                        </div>
                                    </div>

                                    <div className='__cprPr_fxInr'>
                                        <div className='__cp_flex_full'>
                                            <CityDropdownForTrip
                                                label="GST State"
                                                placeholder="GST State"
                                                fetchData={fetchState}
                                                disabled={isDetails ? true : false}
                                                pageSize={50}
                                                selectedItem={form.gst_state}
                                                onChange={(data) => handleOnChange('gst_state', data)}
                                                error={formError["gst_state.label"]}
                                                errorMessage={formError["gst_state.label"]}
                                            />
                                        </div>
                                    </div>

                                    <div className='__cprPr_fxInr'>
                                        <div className='__cp_flex_half'>
                                            <CommonInput
                                                type="text"
                                                label="PAN Number"
                                                placeholder="PAN Number"
                                                name='pan_no'
                                                value={form?.pan_no ? form?.pan_no : ""}
                                                maxLength={10}
                                                focus={!!formError?.pan_no}
                                                disabled={isDetails ? true : false}
                                                error={formError.pan_no}
                                                onChange={({ target: { name, value } }) => {
                                                    const cleanedValue = value.replace(/[^a-zA-Z0-9 ]+/g, "");
                                                    if (typeof handleOnChange === "function") {
                                                        // handleOnChange(name, cleanedValue);
                                                        const upperValue = cleanedValue.toUpperCase();
                                                        handleOnChange(name, upperValue)
                                                    }
                                                }}
                                            />
                                        </div>

                                        <div className='__cp_flex_half'>
                                            <CommonInput
                                                type="text"
                                                label="Fssai License Number"
                                                placeholder="Fssai License Number"
                                                name='fssai_licence'
                                                disabled={isDetails ? true : false}
                                                value={form?.fssai_licence ? form?.fssai_licence : ""}
                                                maxLength={14}
                                                focus={!!formError?.fssai_licence}
                                                error={formError.fssai_licence}
                                                onChange={({ target: { name, value } }) => {
                                                    const cleanedValue = value.replace(/[^a-zA-Z0-9 ]+/g, "");

                                                    if (typeof handleOnChange === "function") {
                                                        // handleOnChange(name, cleanedValue);

                                                        const upperValue = cleanedValue.toUpperCase();
                                                        handleOnChange(name, upperValue)
                                                    }
                                                }}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div>
                                <p className='__cp_head'>Transport Type</p>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px', alignItems: 'center' }}>
                                    {masterData?.company_type?.map((item, index) => (
                                        <Checkbox
                                            key={item._id}
                                            name={item.name}
                                            id={`firm_type${index}`}
                                            isDisabled={isDetails ? true : false}
                                            className="__trkChecks_firm"
                                            handleClick={(e) => handleCheckboxChange(e, item)}
                                            isChecked={form.firm_type?.includes(item._id)}
                                            label={item.name}
                                        />
                                    ))}
                                </div>
                            </div>
                        </div>

                    </div>
                </div>

                <ImageCropModal
                    isOpen={isModalOpen}
                    onClose={handleCloseModal}
                    onCrop={handleCropComplete}
                    imageSrc={originalImage}
                    imageLoading={imageLoading}
                />
            </div>

        </div>
    )
}

export default FirmProfileDetails;